import React from "react";
import { Button } from "react-bootstrap";

/**
 * Статус синхронизации остатков по МП: на каком этапе флоу сбой,
 * причина карантина, инструкции по исправлению, сброс карантина (§4.7, G4/G7).
 * Поддержка WB и Ozon.
 */

export type MpSyncStatusProps = {
  remnant1cUpdatedAt?: string | null;
  mpStockSyncAt?: string | null;
  mpStockActual?: number | null;
  targetStock?: number | null;
  lastSentAt?: string | null;
  needUpdate?: boolean;
  disabled?: boolean | null;
  quarantineAt?: string | null;
  quarantineReason?: string | null;
  syncError?: string | null;
  canSend?: boolean;
  onResetQuarantine?: () => void;
};

type Stage = { label: string; ok: boolean; hint?: string };

const isActive = (iso?: string | null): boolean => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t > Date.now();
};

const reasonLabel = (reason?: string | null): string => {
  switch (reason) {
    case "rate_limit":
      return "слишком частые обновления (лимит МП)";
    case "sku_absent":
      return "товара с таким SKU нет на МП";
    case "error":
      return "ошибка отправки";
    default:
      return reason || "—";
  }
};

const instructionFor = (params: {
  no1c: boolean;
  quarantineReason?: string | null;
  canSend?: boolean;
}): string | null => {
  if (params.no1c)
    return "Нет данных из 1С. Проверьте артикул на лишние пробелы/табы (подсветка слева) и сверьте с выгрузкой 1С.";
  if (params.quarantineReason === "sku_absent")
    return "Товара нет на витрине МП. Добавьте товар на МП (или снимите с распределения). После появления на МП карантин снимется автоматически.";
  if (params.quarantineReason === "rate_limit")
    return "Слишком частые обновления. Подождите ~20 минут — отправка возобновится автоматически.";
  if (params.quarantineReason === "error")
    return "Ошибка отправки на МП. Проверьте текст ошибки ниже; после устранения снимите карантин.";
  if (params.canSend === false)
    return "Укажите корректную формулу распределения.";
  return null;
};

const MpSyncStatus = (props: MpSyncStatusProps) => {
  const quarantineActive = isActive(props.quarantineAt);
  const no1c = !props.remnant1cUpdatedAt;

  const stages: Stage[] = [
    { label: "1С", ok: !no1c, hint: "Получен остаток из 1С" },
    { label: "Факт. МП", ok: Boolean(props.mpStockSyncAt) },
    {
      label: "Расчёт",
      ok: typeof props.targetStock === "number" && props.canSend !== false,
    },
    { label: "Отправка", ok: Boolean(props.lastSentAt) && !quarantineActive },
    { label: "Карантин", ok: !quarantineActive },
  ];

  const instruction = instructionFor({
    no1c,
    quarantineReason: quarantineActive ? props.quarantineReason : null,
    canSend: props.canSend,
  });

  return (
    <div style={{ fontSize: 11, marginTop: 6 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
        {stages.map((s, i) => (
          <span
            key={s.label}
            title={s.hint}
            style={{
              padding: "0 6px",
              borderRadius: 10,
              border: "1px solid",
              borderColor: s.ok ? "#27ae60" : "#c0392b",
              color: s.ok ? "#1e7a45" : "#c0392b",
              background: s.ok ? "#eafaf0" : "#fdecea",
              whiteSpace: "nowrap",
            }}
          >
            {s.ok ? "✓" : "✕"} {s.label}
          </span>
        ))}
      </div>

      {quarantineActive && (
        <div style={{ marginTop: 4, color: "#c0392b" }}>
          Карантин: {reasonLabel(props.quarantineReason)}
          {props.onResetQuarantine && (
            <Button
              size="sm"
              variant="outline-secondary"
              style={{ marginLeft: 8, padding: "0 6px", fontSize: 11 }}
              onClick={props.onResetQuarantine}
            >
              Снять карантин
            </Button>
          )}
        </div>
      )}

      {props.syncError && (
        <div
          style={{ marginTop: 4, color: "#8a6d3b", wordBreak: "break-word" }}
          title={props.syncError}
        >
          Ошибка: {props.syncError.slice(0, 200)}
          {props.syncError.length > 200 ? "…" : ""}
        </div>
      )}

      {instruction && (
        <div style={{ marginTop: 4, color: "#31708f" }}>💡 {instruction}</div>
      )}
    </div>
  );
};

export default MpSyncStatus;
